import { useCallback, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    authenticateResume,
    getQuote,
    getApplication,
    getFinderNumberBySaveContId,
    AuthenticateResumeRequestBody,
} from "services/onlineApplicationService";
import { ConfirmYourIdentityFormData } from "components/card/confirm-your-identity/ConfirmYourIdentityCard";
import { ROUTES } from "routes/constants";
import { useAppDispatch } from "store/hooks";
import { hydrateContactData, markResumedApplication, saveQuoteData } from "store/application/applicationSlice";
import { transformGetDTOAppToContactData } from "mappers/transformGetDTOApp";
import { transformGetQuoteDataToQuoteData } from "utils/quoteUtils";
import { useOffer } from "hooks/useOffer";
import { useProductContext } from "context/ProductContext";
import { GA_DEDUP_KEYS, GA_EVENT_CONFIGS } from "constants/gaEvents";
import { pushGTMEventOnce } from "utils/gaEventDedup";

const DEFAULT_APPLICANT_TYPE = "member";

function getErrorStatus(error: unknown): number | undefined {
    if (error && typeof error === "object" && "status" in error) {
        return (error as Error & { status: number }).status;
    }
    return undefined;
}

/**
 * Transforms form data to the format expected by the authenticate resume API
 */
function transformFormDataToRequestBody(data: ConfirmYourIdentityFormData): Omit<AuthenticateResumeRequestBody, "saveContId"> {
    const { month, day, year } = data.dateOfBirth;
    return {
        lastName: data.lastName,
        dateOfBirth: `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
    };
}

/**
 * Resolves finder number from URL params or by fetching via saveContId
 */
async function resolveFinderNumber(searchParams: URLSearchParams, saveContID: string): Promise<string | undefined> {
    const fromParams = searchParams.get("finderNumber");
    if (fromParams) return fromParams;

    const result = await getFinderNumberBySaveContId(saveContID);
    return result?.finderNumber;
}

/**
 * Fetches and dispatches quote data for a resumed application
 */
async function hydrateQuote(userRef: string, dispatch: ReturnType<typeof useAppDispatch>): Promise<void> {
    const quoteResponse = await getQuote(userRef);
    if (!quoteResponse || Object.keys(quoteResponse).length === 0) return;

    const quoteData = transformGetQuoteDataToQuoteData(DEFAULT_APPLICANT_TYPE, quoteResponse as any, []);
    dispatch(saveQuoteData(quoteData));
}

/**
 * Fetches and dispatches application contact data for a resumed application
 */
async function hydrateApplication(userRef: string, dispatch: ReturnType<typeof useAppDispatch>): Promise<void> {
    const appResponse = await getApplication(userRef);
    if (!appResponse || Object.keys(appResponse).length === 0) return;

    const contactData = transformGetDTOAppToContactData(appResponse as any, DEFAULT_APPLICANT_TYPE);
    dispatch(hydrateContactData(contactData));
}

/**
 * Hook to authenticate resume using saveContID and form data
 */
export const useAuthenticateResume = () => {
    // ========== Hooks ==========
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { isGIWL } = useProductContext();
    const { validateAndProcessOffer } = useOffer();

    // ========== State ==========
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authenticationError, setAuthenticationError] = useState<string | null>(null);

    // ========== Event Handlers ==========

    /**
     * Routes known error statuses to the appropriate page or message
     */
    const handleAuthError = useCallback(
        (error: unknown): false => {
            const status = getErrorStatus(error);
            const saveContID = searchParams.get("save_cont_id");

            if (status === 410) {
                if (saveContID) {
                    pushGTMEventOnce(
                        `${GA_DEDUP_KEYS.ATTEMPT_EXPIRED}:${saveContID}`,
                        GA_EVENT_CONFIGS.SAVE_CONTINUE_ATTEMPT_EXPIRED
                    );
                }
                navigate(ROUTES.UNCOMPLETED_APPLICATION_EXPIRED);
                return false;
            }
            if (status === 423) {
                if (saveContID) {
                    pushGTMEventOnce(
                        `${GA_DEDUP_KEYS.ATTEMPT_LOCKED}:${saveContID}`,
                        GA_EVENT_CONFIGS.SAVE_CONTINUE_ATTEMPT_LOCKED
                    );
                }
                navigate(ROUTES.AUTHENTICATION_FAILED);
                return false;
            }

            const errorMessage =
                error instanceof Error
                    ? error.message
                    : "Your information does not match. Please try again or start a new application.";
            setAuthenticationError(errorMessage);
            if (saveContID) {
                pushGTMEventOnce(`${GA_DEDUP_KEYS.ATTEMPT_ERROR}:${saveContID}`, GA_EVENT_CONFIGS.SAVE_CONTINUE_ATTEMPT_ERROR);
            }
            console.error("[useAuthenticateResume] Error:", error);
            return false;
        },
        [navigate, searchParams]
    );

    /**
     * Authenticates the resume using saveContID from URL and form data
     */
    const authenticate = useCallback(
        async (formData: ConfirmYourIdentityFormData): Promise<boolean> => {
            const saveContID = searchParams.get("save_cont_id");

            if (!saveContID) {
                setAuthenticationError("saveContID not found in URL (expected ?save_cont_id=...)");
                console.error("[useAuthenticateResume] saveContID not found. Current URL:", globalThis.window.location.href);
                return false;
            }

            setIsAuthenticating(true);
            setAuthenticationError(null);

            try {
                const requestBody = transformFormDataToRequestBody(formData);
                const response = await authenticateResume({ ...requestBody, saveContId: saveContID });

                if (!response.isAuthenticated) {
                    pushGTMEventOnce(
                        `${GA_DEDUP_KEYS.ATTEMPT_INVALID_IDENTITY}:${saveContID}`,
                        GA_EVENT_CONFIGS.SAVE_CONTINUE_ATTEMPT_INVALID_IDENTITY
                    );
                    return false;
                }

                if (isGIWL) {
                    // GIWL has no finderNumber — hydrate directly via saveContId
                    await Promise.allSettled([hydrateQuote(saveContID, dispatch), hydrateApplication(saveContID, dispatch)]);

                    dispatch(markResumedApplication());
                    navigate(ROUTES.QUOTE);
                    pushGTMEventOnce(
                        `${GA_DEDUP_KEYS.ATTEMPT_SUCCESS}:${saveContID}`,
                        GA_EVENT_CONFIGS.SAVE_CONTINUE_ATTEMPT_SUCCESS
                    );
                    pushGTMEventOnce(`${GA_DEDUP_KEYS.RESUMED}:${saveContID}`, GA_EVENT_CONFIGS.SAVE_CONTINUE_RESUMED);
                    return true;
                }

                const finderNumber = await resolveFinderNumber(searchParams, saveContID);
                if (!finderNumber) {
                    setAuthenticationError("Unable to retrieve finder number");
                    pushGTMEventOnce(
                        `${GA_DEDUP_KEYS.ATTEMPT_NOT_FOUND}:${saveContID}`,
                        GA_EVENT_CONFIGS.SAVE_CONTINUE_ATTEMPT_NOT_FOUND
                    );
                    return false;
                }

                const offerResult = await validateAndProcessOffer(finderNumber, false);
                if (!offerResult.success) return false;

                // Hydrate quote and app data — failures are expected for new applications
                await Promise.allSettled([hydrateQuote(finderNumber, dispatch), hydrateApplication(finderNumber, dispatch)]);

                dispatch(markResumedApplication());
                navigate(ROUTES.QUOTE);
                pushGTMEventOnce(
                    `${GA_DEDUP_KEYS.ATTEMPT_SUCCESS}:${saveContID}`,
                    GA_EVENT_CONFIGS.SAVE_CONTINUE_ATTEMPT_SUCCESS
                );
                pushGTMEventOnce(`${GA_DEDUP_KEYS.RESUMED}:${saveContID}`, GA_EVENT_CONFIGS.SAVE_CONTINUE_RESUMED);
                return true;
            } catch (error) {
                return handleAuthError(error);
            } finally {
                setIsAuthenticating(false);
            }
        },
        [isGIWL, searchParams, navigate, dispatch, validateAndProcessOffer, handleAuthError]
    );

    return {
        authenticate,
        isAuthenticating,
        authenticationError,
    };
};
